import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { searchClients } from '../services/clientService';
import { terminology } from '../config/terminology';

const MIN_QUERY_LENGTH = 3; // matches searchClients' own no-op threshold, avoids a wasted call
const DEBOUNCE_MS = 300;
const MAX_RESULTS = 8;

/**
 * Debounced type-to-search combobox over Suppliers (Firestore `clients`), backed by the
 * existing searchClients (POST /client/search). Follows the ARIA 1.2 "combobox with listbox
 * popup" pattern: DOM focus stays on the text input, options are never individually focusable,
 * ArrowUp/ArrowDown move a highlightedIndex reflected via aria-activedescendant. Selecting with
 * the mouse uses onMouseDown preventDefault (so the input never blurs) + onClick (does the
 * actual selection), sidestepping the onBlur-vs-onMouseDown race.
 *
 * Once a Supplier is selected, the input is swapped for a "chip" showing its name plus a clear
 * button — ported from jims-mowing-supplier's Autosuggest.
 *
 * @param {object|null} selectedSupplier - currently selected Supplier, shaped { id, clientName }
 * @param {(supplier: {id, clientName}) => void} onSelect
 * @param {() => void} onClear
 * @param {string} [id] - id for the search input, so a sibling <label htmlFor> can target it
 * @param {string} [placeholder]
 * @param {boolean} [disabled]
 */
const SupplierAutocomplete = ({ selectedSupplier, onSelect, onClear, id, placeholder, disabled = false }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const listboxId = useId();
  const optionId = (index) => `${listboxId}-option-${index}`;
  const clearButtonRef = useRef(null);
  const wasSelectedRef = useRef(Boolean(selectedSupplier));

  // Monotonic request id: a newer query invalidates any still-in-flight older one, so a
  // slow response can't clobber a faster, more recent one.
  const requestIdRef = useRef(0);
  const debounceTimerRef = useRef(null);

  const runSearch = useCallback(async (searchString) => {
    const requestId = ++requestIdRef.current;
    setIsSearching(true);
    setSearchError(false);
    // searchClients swallows its own errors into { clients: [], error } rather than throwing.
    const result = await searchClients({ searchString, pageSize: MAX_RESULTS });
    if (requestId !== requestIdRef.current) return; // superseded - ignore
    if (result.error) {
      setResults([]);
      setSearchError(true);
    } else {
      setResults((result.clients || []).slice(0, MAX_RESULTS));
      setHighlightedIndex(-1);
    }
    setShowResults(true);
    setIsSearching(false);
  }, []);

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    if (query.trim().length < MIN_QUERY_LENGTH) {
      requestIdRef.current += 1; // invalidate any in-flight request
      debounceTimerRef.current = setTimeout(() => {
        setResults([]);
        setShowResults(false);
        setIsSearching(false);
        setHighlightedIndex(-1);
      }, 0);
      return () => clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => runSearch(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(debounceTimerRef.current);
  }, [query, runSearch]);

  // Selecting a Supplier swaps the whole combobox subtree for a "selected" chip, which would
  // otherwise drop keyboard focus to document.body. Move it onto the chip's Clear button
  // instead, but only on the transition into "selected" (not on every re-render).
  useEffect(() => {
    if (selectedSupplier && !wasSelectedRef.current) {
      clearButtonRef.current?.focus();
    }
    wasSelectedRef.current = Boolean(selectedSupplier);
  }, [selectedSupplier]);

  const handleSelect = (supplier) => {
    requestIdRef.current += 1; // invalidate any in-flight request
    setQuery('');
    setResults([]);
    setShowResults(false);
    setHighlightedIndex(-1);
    onSelect(supplier);
  };

  const handleClear = () => {
    requestIdRef.current += 1;
    setQuery('');
    setResults([]);
    setShowResults(false);
    setHighlightedIndex(-1);
    onClear();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (showResults) {
        e.preventDefault();
        // The dropdown consumes this Escape - without stopping it, the enclosing modal's
        // document-level handler would close the whole modal on the same keypress.
        e.stopPropagation();
        setShowResults(false);
        setHighlightedIndex(-1);
      }
      return;
    }

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (results.length === 0) return;
      e.preventDefault();
      // Escape leaves `results` in state but closes the popup - reopen on the next arrow
      // press instead of forcing the user to retype to get back in.
      if (!showResults) {
        setShowResults(true);
        setHighlightedIndex(e.key === 'ArrowDown' ? 0 : results.length - 1);
        return;
      }
      setHighlightedIndex((i) => {
        if (e.key === 'ArrowDown') return i + 1 >= results.length ? 0 : i + 1;
        return i - 1 < 0 ? results.length - 1 : i - 1;
      });
      return;
    }

    if (e.key === 'Enter' && showResults && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelect(results[highlightedIndex]);
    }
  };

  if (selectedSupplier) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm">
        <span className="truncate text-gray-900">{selectedSupplier.clientName}</span>
        <button
          ref={clearButtonRef}
          type="button"
          onClick={handleClear}
          disabled={disabled}
          className="shrink-0 rounded p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:ring-offset-2"
          aria-label={`Clear selected ${terminology.client.singular.toLowerCase()}`}
        >
          <FaTimes aria-hidden="true" />
        </button>
      </div>
    );
  }

  // The <ul role="listbox"> only exists once there are options to show it for - aria-controls
  // and aria-activedescendant must never point at an id that isn't currently in the DOM.
  const listboxRendered = showResults && !isSearching && !searchError && results.length > 0;

  return (
    <div className="relative">
      <div className="relative">
        <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
        <input
          id={id}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-expanded={showResults}
          aria-controls={listboxRendered ? listboxId : undefined}
          aria-activedescendant={listboxRendered && highlightedIndex >= 0 ? optionId(highlightedIndex) : undefined}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (results.length > 0 || searchError) setShowResults(true); }}
          onBlur={() => setShowResults(false)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className="block w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:ring-offset-2 focus:border-jims-cyan disabled:bg-gray-100 disabled:text-gray-500"
        />
      </div>

      {showResults && (
        <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
          {isSearching && <p className="px-3 py-2 text-sm text-gray-500">Searching…</p>}
          {!isSearching && searchError && (
            <p className="px-3 py-2 text-sm text-red-600">Could not search {terminology.client.plural.toLowerCase()}. Please try again.</p>
          )}
          {!isSearching && !searchError && results.length === 0 && (
            <p className="px-3 py-2 text-sm text-gray-500">No {terminology.client.plural.toLowerCase()} found.</p>
          )}
          {listboxRendered && (
            <ul id={listboxId} role="listbox" aria-label={`${terminology.client.singular} results`} className="max-h-60 overflow-auto text-sm">
              {results.map((supplier, index) => (
                <li
                  key={supplier.id}
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === highlightedIndex}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => handleSelect(supplier)}
                  className={`block cursor-pointer px-3 py-2 text-left ${
                    index === highlightedIndex ? 'bg-jims-cyan text-white' : 'hover:bg-gray-100'
                  }`}
                >
                  {supplier.clientName}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default SupplierAutocomplete;
